import React from "react";
import * as Yup from 'yup';
import { TipoInterventoSelector } from "../../tipiIntervento/components/tipoInterventoSelector";
import { Forms } from "../../common/components/CmForms";
import { ImpiantoSelector } from "../../impianti/components/ImpiantoSelector";
import { CustomersAutocomplete } from "../../customers/components/CustomersAutocomplete";
import { TechnicianSelectorNoFormGroup } from "../../technicians/components/TechnicianSelector";
import { DateTimePicker } from "../../common/components/DateTimePicker";
import DatesHelper from "../../common/datesHelper";
import InterventiStore from "../interventiStore";
import InterventiActions from "../InterventiActions";
import { Loading } from "../../common/components/loading";
import { ApparecchioCategory, ApparecchioFamily, InterventoData, InterventoDto } from "../../generated";
import { DeleteButton } from "../../common/components/DeleteButton";
import { InterventoDetail } from "./InterventoDetail";
import { links } from "../../routes";
import { withApparecchioCateogories } from "../../impianti/withApparecchioCategories";
const { Form, FormText, FormCheck, Submit, Cancel } = Forms<InterventoData>()
import { Dictionary } from "../../common/AbstractStores";
import { ImpiantoLightDataComponent } from "../../impianti/components/impiantoLightData";
interface Props {
    idIntervento?: number;
    intervento: InterventoData;
    onSubmit: (intervento: InterventoData) => void;
    onCancel: () => void;
    onDelete?: () => void;
    categories: Dictionary<ApparecchioCategory>
}

const validationSchema = Yup.object<Partial<InterventoData>>({
    tipoIntervento: Yup
        .mixed()
        .test("tipo-intervento-required", "Campo obbligatorio", value => value?.idTipoIntervento > 0),
    dataIntervento: Yup
        .date()
        .min(new Date(2000, 1, 1), "Ultimo controllo non valido")
        .required("Campo obbligatorio"),
    dataFineIntervento: Yup
        .date()
        .min(new Date(2000, 1, 1), "Ultimo controllo non valido"),
    idCliente: Yup
        .number()
        .required("Campo obbligatorio")
})

const CollisionWarning = ({ idIntervento, from, to, idTechnician }: { idIntervento?: number, idTechnician?: number, from: Date, to?: Date }) => {
    if (!idTechnician)
        return null;
    if (!to)
        return null;

    const [interventi, setInterventi] = React.useState<InterventoDto[]>([]);
    React.useEffect(() => {
        InterventiActions
            .getByTechnician(idTechnician, from, to)
            .then(x => setInterventi(x.filter(i => i.dataFineIntervento && i.idIntervento != idIntervento)))
    }, [from, to, idTechnician])
    if (interventi.length == 0)
        return null;
    return <div style={{ fontSize: "0.9em" }}><i className="text-warning fas fa-exclamation-circle" /> Il tecnico ha un altro intervento programmato dalle {DatesHelper.getReadableDateTimeString(interventi[0].dataIntervento)} alle {DatesHelper.getReadableDateTimeString(interventi[0].dataFineIntervento!)}</div>
}

const InterventoForm = withApparecchioCateogories((props: Props) => {

    const { intervento } = props;
    return (<Form
        onSubmit={props.onSubmit}
        initialValues={intervento}
        validationSchema={validationSchema}
    >
        {
            ({ set, values, errors }) =>
                <>
                    <div className="row">
                        {
                            <div className="col-12 col-md-6">
                                <CustomersAutocomplete
                                    readOnly={!!intervento.idCliente}
                                    label="Cliente"
                                    onChange={cliente => cliente && set("idCliente", cliente?.idCliente)}
                                    value={values.idCliente}
                                />
                            </div>
                        }
                        {
                            <div className="col-12 col-md-6">
                                <ImpiantoSelector
                                    onChange={i => set("idImpianto", i?.id)}
                                    value={values.idImpianto || null}
                                    idCliente={values.idCliente}
                                    allowNull={true}
                                />
                            </div>
                        }
                        <div className="col-12 col-md-6">
                            <TipoInterventoSelector
                                label="Tipo Intervento"
                                onChange={tipoIntervento => tipoIntervento && set("tipoIntervento", tipoIntervento)}
                                value={values.tipoIntervento}
                                error={errors.tipoIntervento}
                            />
                        </div>
                        <div className="col-12 col-md-6 d-flex flex-column">
                            <FormCheck
                                name="controlloStandard"
                                label="Manutenzione Ordinaria"
                            />
                            {values.idImpianto && <ImpiantoLightDataComponent
                                idCliente={values.idCliente}
                                id={values.idImpianto}
                                template={impianto =>
                                    props.categories[impianto.type]?.family == ApparecchioFamily.ClimatizzazioneElettrico
                                        ? <div />
                                        : <>
                                            <FormCheck
                                                name="controlloCombustione"
                                                label="Controllo Combustione"
                                            />
                                            <FormCheck
                                                name="applicazioneBollino"
                                                label="Applicato Bollino"
                                            />
                                        </>}
                            />}
                        </div>
                        <div className="col-12 col-md-2">
                            <FormText
                                name="identificativoIntervento"
                                label="Identificativo"
                            />
                        </div>
                        <div className="col-12 col-md-4">
                            <div className="mb-3">
                                <TechnicianSelectorNoFormGroup
                                    label="Tecnico assegnato"
                                    onChange={technician => set("idTechnician", technician?.id)}
                                    value={values.idTechnician ?? null}
                                    error={errors.idTechnician}
                                />
                                <CollisionWarning
                                    idIntervento={props.idIntervento}
                                    idTechnician={values.idTechnician}
                                    from={values.dataIntervento}
                                    to={values.dataFineIntervento}
                                />
                            </div>
                        </div>
                        <div className="col-12 col-md-6">
                            <div className="row">
                                <div className="col-12 col-md-6">
                                    <DateTimePicker
                                        label="Dalle ore"
                                        onSelectDate={date => {
                                            const d = date ?? DatesHelper.Today();
                                            set("dataIntervento", d);
                                        }}
                                        onCalendarClose={() => {
                                            if (values.dataIntervento && (!values.dataFineIntervento || values.dataFineIntervento < values.dataIntervento))
                                                set("dataFineIntervento", DatesHelper.AddHours(values.dataIntervento, 1))
                                        }}
                                        value={values.dataIntervento}
                                        error={errors.dataIntervento}
                                    />
                                </div>
                                <div className="col-12 col-md-6">
                                    <DateTimePicker
                                        label="alle ore"
                                        onSelectDate={date => {
                                            const d = date ?? new Date();
                                            set("dataFineIntervento", d);

                                        }}
                                        onCalendarClose={() => {
                                            if (!values.dataFineIntervento)
                                                return;
                                            if (values.dataFineIntervento != null && (!values.dataIntervento || values.dataIntervento > values.dataFineIntervento))
                                                set("dataIntervento", DatesHelper.AddHours(values.dataFineIntervento, -1))
                                        }}
                                        value={values.dataFineIntervento}
                                        error={errors.dataFineIntervento}

                                    />
                                </div>
                            </div>
                        </div>
                        <div className="col-12">
                            <FormText
                                name="oggettoIntervento"
                                label="Oggetto Intervento"
                            />
                        </div>
                        <div className="col-12">
                            <InterventoDetail
                                details={values.details ?? []}
                                onChange={details => set("details", details)}
                            />
                        </div>
                    </div>
                    <div className={"form-actions mb-3"}>
                        <Submit />
                        {props.idIntervento && <>{" "}<a className="btn btn-secondary" href={links.rapportinoUrl(props.idIntervento)} target="_blank"><i className="fas fa-file" /> Rapportino</a></>}
                        {" "}{props.onDelete != null && <DeleteButton onDelete={props.onDelete} />}
                        {" "}{props.onCancel != null && <Cancel onCancel={props.onCancel} />}
                    </div>
                </>
        }
    </Form>
    );
})(x => x.allByKey(), "categories")

export const InterventoEdit = (
    { idClienteStr, id, idImpianto, onCancel, afterSave, afterDelete }:
        {
            idClienteStr: string,
            id: number,
            idImpianto: number | null,
            onCancel: () => void,
            afterSave?: (intervento: InterventoData) => void;
            afterDelete?: (intervento: InterventoData) => void;
        }) => {
    let interventoData;
    const interventi = InterventiStore.useForCliente(+idClienteStr);
    const intervento = interventi?.find(x => x.idIntervento == id);
    if (!intervento)
        return <Loading />;
    interventoData = new InterventoData(intervento);
    if (idImpianto)
        interventoData.idImpianto = idImpianto;

    const save = async (intData: InterventoData) => {
        await InterventiActions.update(intData, id);
        afterSave?.(intervento);
    }
    const onDelete = async () => {
        await InterventiActions.delete(+idClienteStr, id);
        afterDelete?.(intervento);
    }
    return (
        <InterventoForm
            idIntervento={id}
            intervento={interventoData}
            onSubmit={save}
            onCancel={onCancel}
            onDelete={onDelete}
        />
    );
}


export const InterventoNew = (props: {
    idImpianto: number | null,
    idCliente: number | null,
    from?: Date,
    to?: Date,
    afterSave: (intervento: InterventoDto) => void,
    onCancel: () => void,
}) => {
    const { idCliente, idImpianto, from, onCancel, to, afterSave } = props;
    let interventoData = new InterventoData()
    if (idImpianto)
        interventoData.idImpianto = +idImpianto;
    if (idCliente)
        interventoData.idCliente = +idCliente;
    if (from)
        interventoData.dataIntervento = from;
    if (to)
        interventoData.dataFineIntervento = to;

    const save = async (intData: InterventoData) => {
        const intervento = await InterventiActions.add(intData);
        afterSave(intervento);
    }
    return (
        <InterventoForm
            intervento={interventoData}
            onSubmit={save}
            onCancel={onCancel}
        />
    );
}
